import React, { useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { PenLine, Type, Eraser } from 'lucide-react';

/**
 * Wiederverwendbares Unterschriftsfeld (Zeichnen oder Tippen).
 * Liefert per ref.getSignaturePng() die Signatur als Data-URL (oder null).
 */
export const SignaturePad = forwardRef(({ defaultName = '' }, ref) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasDrawn = useRef(false);
  const [mode, setMode] = useState('draw');
  const [typed, setTyped] = useState(defaultName);

  const clearCanvas = () => {
    const c = canvasRef.current; if (!c) return;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    hasDrawn.current = false;
  };
  const pos = (e) => {
    const c = canvasRef.current, r = c.getBoundingClientRect(), t = e.touches ? e.touches[0] : e;
    return { x: (t.clientX - r.left) * (c.width / r.width), y: (t.clientY - r.top) * (c.height / r.height) };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext('2d'); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = '#0a2870'; ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.stroke(); hasDrawn.current = true; };
  const end = () => { drawing.current = false; };
  const typedPng = () => { const c = document.createElement('canvas'); c.width = 500; c.height = 160; const ctx = c.getContext('2d'); ctx.fillStyle = '#0a2870'; ctx.font = 'italic 60px "Segoe Script","Brush Script MT",cursive'; ctx.textBaseline = 'middle'; ctx.fillText(typed, 20, 90); return c.toDataURL('image/png'); };

  useImperativeHandle(ref, () => ({
    getSignaturePng: () => {
      if (mode === 'draw') return hasDrawn.current ? canvasRef.current.toDataURL('image/png') : null;
      return typed.trim() ? typedPng() : null;
    },
  }), [mode, typed]);

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Button type="button" size="sm" variant={mode === 'draw' ? 'default' : 'outline'} onClick={() => setMode('draw')} data-testid="pad-mode-draw"><PenLine className="h-4 w-4 mr-1" /> Zeichnen</Button>
        <Button type="button" size="sm" variant={mode === 'type' ? 'default' : 'outline'} onClick={() => setMode('type')} data-testid="pad-mode-type"><Type className="h-4 w-4 mr-1" /> Tippen</Button>
      </div>
      {mode === 'draw' ? (
        <div className="space-y-2">
          <div className="border rounded-lg bg-muted/20">
            <canvas ref={canvasRef} width={500} height={160} className="w-full touch-none rounded-lg" style={{ height: 160 }}
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="pad-canvas" />
          </div>
          <Button type="button" size="sm" variant="ghost" onClick={clearCanvas}><Eraser className="h-4 w-4 mr-1" /> Löschen</Button>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label>Unterschrift (getippt)</Label>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Name tippen" data-testid="pad-typed" />
          <div className="border rounded-lg p-3 text-center bg-muted/20" style={{ fontFamily: '"Segoe Script","Brush Script MT",cursive', fontStyle: 'italic', fontSize: 32, color: '#0a2870' }}>{typed || 'Vorschau'}</div>
        </div>
      )}
    </div>
  );
});
